import { api } from "./apiClient";
import {
  AvailableField,
  BudgetGroupTemplateEntry,
  ClientSpreadsheetRecord,
  FieldDomain,
  FieldMapping,
  GeneratePreviewResponse,
  GenerateSpreadsheetRequest,
  SpreadsheetCategory,
  SpreadsheetWarning,
  TemplateInspection,
  UploadTemplateResponse,
} from "../types/clientSpreadsheet.type";

export default class ClientSpreadsheetService {
  static async getFields(domain?: FieldDomain): Promise<AvailableField[]> {
    return api
      .get("/client-spreadsheet/fields", {
        params: domain ? { domain } : undefined,
      })
      .then((r) => r.data);
  }

  static async getCategories(): Promise<SpreadsheetCategory[]> {
    return api.get("/client-spreadsheet/categories").then((r) => r.data);
  }

  static async getRecord(clientId: string): Promise<ClientSpreadsheetRecord> {
    return api.get(`/client-spreadsheet/${clientId}`).then((r) => r.data);
  }

  static async inspect(clientId: string): Promise<TemplateInspection> {
    return api
      .get(`/client-spreadsheet/${clientId}/inspect`)
      .then((r) => r.data);
  }

  static async uploadTemplate(
    clientId: string,
    file: File,
    resetMappings: boolean
  ): Promise<UploadTemplateResponse> {
    const formData = new FormData();
    formData.append(
      "file",
      new Blob([file], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      file.name
    );
    return api
      .post(`/client-spreadsheet/${clientId}/template`, formData, {
        params: { resetMappings },
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  }

  static async saveMappings(
    clientId: string,
    fieldMappings: FieldMapping[]
  ): Promise<ClientSpreadsheetRecord> {
    return api
      .put(`/client-spreadsheet/${clientId}/mappings`, { fieldMappings })
      .then((r) => r.data);
  }

  /**
   * Substitui a lista inteira de agrupamentos padrao — a ORDEM do array e a
   * ordem em que eles aparecem no orcamento.
   */
  static async saveBudgetGroupTemplate(
    clientId: string,
    budgetGroupTemplate: BudgetGroupTemplateEntry[]
  ): Promise<ClientSpreadsheetRecord> {
    return api
      .put(`/client-spreadsheet/${clientId}/budget-group-template`, {
        budgetGroupTemplate,
      })
      .then((r) => r.data);
  }

  static async deleteTemplate(clientId: string): Promise<void> {
    return api
      .delete(`/client-spreadsheet/${clientId}/template`)
      .then((r) => r.data);
  }

  static async generatePreview(
    clientId: string,
    body: GenerateSpreadsheetRequest
  ): Promise<GeneratePreviewResponse> {
    return api
      .post(`/client-spreadsheet/${clientId}/generate/preview`, body)
      .then((r) => r.data);
  }

  static async generate(
    clientId: string,
    body: GenerateSpreadsheetRequest
  ): Promise<{ warnings: SpreadsheetWarning[]; filledMappings: number }> {
    let res: any;
    try {
      res = await api.post(
        `/client-spreadsheet/${clientId}/generate`,
        body,
        { responseType: "blob" }
      );
    } catch (err: any) {
      // Quando responseType:"blob" o corpo do erro e um Blob; convertemos para JSON antes de re-lancar.
      const rawData = err?.response?.data;
      if (rawData instanceof Blob) {
        const text = await rawData.text();
        try {
          err.response.data = JSON.parse(text);
        } catch {
          err.response.data = { message: text };
        }
      }
      throw err;
    }

    const warningsHeader = res.headers["x-spreadsheet-warnings"] as
      | string
      | undefined;
    let meta: { warnings: SpreadsheetWarning[]; filledMappings: number } = {
      warnings: [],
      filledMappings: 0,
    };
    if (warningsHeader) {
      try {
        meta = JSON.parse(decodeURIComponent(warningsHeader));
      } catch {
        // header malformado, ignora
      }
    }

    const blob = new Blob([res.data as BlobPart], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const contentDisposition =
      (res.headers["content-disposition"] as string) || "";
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch
      ? decodeURIComponent(filenameMatch[1])
      : "planilha-cliente.xlsx";

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);

    return meta;
  }
}
